import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, BehaviorSubject } from 'rxjs';
import { AuthService } from '../../core/auth/services/auth.service';
import { IFullProject, IProjects, IRepositories } from '../models';
import { ISessionToken } from '@core/models';
import { CacheService } from '../../shared/services/cache/cache.service';
import config from '../../../config';

@Injectable({
  providedIn: 'root',
})
export class GithubService {
  apiUrl = config.API_GATEWAY_ENDPOINT;
  prid = '';

  /** Observable to track if data is currently being refreshed */
  private _isRefreshingSubject = new BehaviorSubject<boolean>(false);
  readonly isRefreshing$ = this._isRefreshingSubject.asObservable();

  /** Cache TTL: 9 minutes */
  private readonly CACHE_TTL = 9 * 60 * 1000;

  constructor(
    readonly http: HttpClient,
    readonly _authService: AuthService,
    readonly _cacheService: CacheService,
  ) {
    this._authService.amplifyGetUserSession().forEach((session: any) => {
      this.prid = this._extractPridFromSession(session);
    });
  }

  /**
   * Fetch complete project data with caching and background refresh
   * This is the main method to use - it returns cached data immediately
   * while refreshing in the background
   *
   * @param forceRefresh - If true, bypass cache and fetch fresh data
   * @returns Object with data and metadata about cache status
   */
  async fetchCompleteProjectData(forceRefresh = false): Promise<{
    data: IFullProject[];
    fromCache: boolean;
    isRefreshing: boolean;
  }> {
    const cacheKey = `projects_${this.prid}`;

    // If force refresh, clear cache first
    if (forceRefresh) {
      this._cacheService.remove(cacheKey);
    }

    // Check if we have cached data
    const cachedData = this._cacheService.get<IFullProject[]>(cacheKey);

    if (cachedData && !forceRefresh) {
      // Return cached data immediately
      const result = {
        data: cachedData,
        fromCache: true,
        isRefreshing: false,
      };

      // Start background refresh (fire and forget)
      this._refreshInBackground(cacheKey);

      return result;
    }

    // No cache or forced refresh - fetch fresh data
    this._isRefreshingSubject.next(true);

    try {
      const freshData = await this._fetchCompleteData();
      this._cacheService.set(cacheKey, freshData, {
        ttl: this.CACHE_TTL,
        persistent: false, // Use sessionStorage
      });

      return {
        data: freshData,
        fromCache: false,
        isRefreshing: false,
      };
    } catch (error) {
      console.error('Error fetching complete project data:', error);
      throw error;
    } finally {
      this._isRefreshingSubject.next(false);
    }
  }

  /**
   * Refresh data in background without blocking
   */
  private _refreshInBackground(cacheKey: string): void {
    // Don't start another refresh if one is already in progress
    if (this._isRefreshingSubject.value) {
      return;
    }

    this._isRefreshingSubject.next(true);

    this._fetchCompleteData()
      .then((freshData) => {
        this._cacheService.set(cacheKey, freshData, {
          ttl: this.CACHE_TTL,
          persistent: false,
        });
      })
      .catch((error) => {
        console.error('❌ Background refresh failed:', error);
      })
      .finally(() => {
        this._isRefreshingSubject.next(false);
      });
  }

  /**
   * Execute the complete data pipeline:
   * 1. Fetch repositories
   * 2. Fetch content for each repository
   * 3. Fetch EOL stack data
   */
  private async _fetchCompleteData(): Promise<IFullProject[]> {
    // Step 1: Get repositories list
    const repositories = await this.fetchRepositories();

    // Step 2: Get content for each repository from GitHub
    const projects = await this._fetchContentRepositories(repositories);

    // Step 3: Get EOL stack data
    const fullProjects = await this._fetchContentStackEOL(projects);

    return fullProjects;
  }

  /**
   * Clear all cached project data
   */
  clearCache(): void {
    const cacheKey = `projects_${this.prid}`;
    this._cacheService.remove(cacheKey);
  }

  async fetchRepositories(): Promise<IRepositories> {
    try {
      const response = await lastValueFrom(
        this.http.get<IRepositories>(`${this.apiUrl}/github/${this.prid}`),
      );
      return response;
    } catch (error) {
      console.error('Error fetching repositories:', error);
      throw error;
    }
  }

  async _fetchContentRepositories(
    ec2Repositories: IRepositories,
  ): Promise<IProjects> {
    try {
      const repositories = ec2Repositories.ec2Repositories;
      const keys = Object.keys(repositories);

      // Only split into chunks if there are more than 8 repositories
      if (keys.length > 8) {
        const ec2RepositoriesChunks = this._splitIntoChunks(ec2Repositories, 2);
        const responses = await Promise.all(
          ec2RepositoriesChunks.map((chunk) =>
            this.fetchContentRepositories(chunk),
          ),
        );
        return this._mergeResponsesFromContentRepositories(responses);
      } else {
        // If there are 8 or less, make a single request
        return await this.fetchContentRepositories(ec2Repositories);
      }
    } catch (error) {
      console.error('Error fetching content repositories:', error);
      throw error;
    }
  }

  async fetchContentRepositories(chunk: IRepositories): Promise<IProjects> {
    return lastValueFrom(
      this.http.post<IProjects>(`${this.apiUrl}/github`, chunk),
    );
  }

  _mergeResponsesFromContentRepositories(responses: IProjects[]): IProjects {
    const merged: IProjects = {};

    responses.forEach((response) => {
      Object.assign(merged, response);
    });

    return merged;
  }

  async _fetchContentStackEOL(projects: IProjects): Promise<IFullProject[]> {
    try {
      const keys = Object.keys(projects);

      // Only split into chunks if there are more than 8 repositories
      if (keys.length > 8) {
        const chunks = this._splitIntoChunks({ ec2Repositories: projects }, 2);
        const responses = await Promise.all(
          chunks.map((chunk) =>
            this.fetchContentStackEOL(chunk.ec2Repositories),
          ),
        );
        return this._mergeArrayResponses(responses);
      } else {
        // If there are 8 or less, make a single request
        return await this.fetchContentStackEOL(projects);
      }
    } catch (error) {
      console.error('Error fetching stack EOL:', error);
      throw error;
    }
  }

  async fetchContentStackEOL(projects: IProjects): Promise<IFullProject[]> {
    return lastValueFrom(
      this.http.post<IFullProject[]>(`${this.apiUrl}/eol`, {
        ec2Repositories: projects,
      }),
    );
  }

  _mergeArrayResponses(responses: IFullProject[][]): IFullProject[] {
    return responses.flat();
  }

  async fetchProjects(): Promise<IRepositories> {
    try {
      const response = await lastValueFrom(
        this.http.get<IRepositories>(`${this.apiUrl}/projects`),
      );
      return response;
    } catch (error) {
      console.error('Error fetching repositories:', error);
      throw error;
    }
  }

  _splitIntoChunks(data: any, numberOfChunks: number): any[] {
    const repositories = data.ec2Repositories;
    const keys = Object.keys(repositories);
    const chunkSize = Math.ceil(keys.length / numberOfChunks);
    const chunks: any[] = [];

    for (let i = 0; i < numberOfChunks; i++) {
      const start = i * chunkSize;
      const end = start + chunkSize;
      const chunkKeys = keys.slice(start, end);

      const chunk: any = { ec2Repositories: {} };
      chunkKeys.forEach((key) => {
        chunk.ec2Repositories[key] = repositories[key];
      });

      chunks.push(chunk);
    }

    return chunks;
  }

  _extractPridFromSession(session: ISessionToken): string {
    if (session?.tokens?.accessToken?.payload?.username) {
      const pridToken = session.tokens.accessToken.payload.username;
      const email = pridToken.split('@')[0];
      return email.split('_')[1] || email;
    }
    return '';
  }
}
